import { ArrowBackIcon, ChatIcon, CheckCircleIcon, DeleteIcon, EditIcon, EmailIcon, WarningIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { bookingService } from "../services/bookingService";
import { eventService } from "../services/eventService";
import { ListaAttesa, Prenotazione } from "../types/Booking";
import { Evento } from "../types/Event";

const EventDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [evento, setEvento] = useState<Evento | null>(null);
  const [prenotazioni, setPrenotazioni] = useState<Prenotazione[]>([]);
  const [listaAttesa, setListaAttesa] = useState<ListaAttesa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [inviting, setInviting] = useState<number | null>(null);
  const [resending, setResending] = useState<number | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<{
    type: "prenotazione" | "listaAttesa";
    id: number;
    name: string;
    isCancelled?: boolean;
  } | null>(null);

  const [inviteTarget, setInviteTarget] = useState<{
    listaAttesaId: number;
    name: string;
    postiDisponibili: number;
    postiRichiesti: number;
  } | null>(null);

  const [noteTarget, setNoteTarget] = useState<{ name: string; note: string } | null>(null);
  const { isOpen: isNoteOpen, onOpen: onNoteOpen, onClose: onNoteClose } = useDisclosure();
  const [sendingReminders, setSendingReminders] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertTarget, setConvertTarget] = useState<{
    listaAttesaId: number;
    name: string;
    email: string;
    postiDisponibili: number;
    postiRichiesti: number;
  } | null>(null);
  const { isOpen: isConvertOpen, onOpen: onConvertOpen, onClose: onConvertClose } = useDisclosure();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isInviteDialogOpen, 
    onOpen: onInviteDialogOpen, 
    onClose: onInviteDialogClose 
  } = useDisclosure();

  const loadData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [eventoData, bookingsData] = await Promise.all([
        eventService.getEvento(parseInt(id)),
        bookingService.getEventoPrenotazioni(parseInt(id)),
      ]);
      setEvento(eventoData);
      setPrenotazioni(bookingsData.prenotazioni || []);
      setListaAttesa(bookingsData.listaAttesa || []);
    } catch (err: any) {
      setError(err.message || "Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteClick = (
    type: "prenotazione" | "listaAttesa",
    id: number,
    name: string,
    isCancelled: boolean = false
  ) => {
    setDeleteTarget({ type, id, name, isCancelled });
    onOpen();
  };

  const handleInvite = async (listaAttesaId: number, name: string, forceInvite: boolean = false) => {
    if (!forceInvite && !window.confirm(`Vuoi inviare un invito a ${name}?`)) {
      return;
    }

    try {
      setInviting(listaAttesaId);
      const result = await bookingService.inviteListaAttesa(listaAttesaId, forceInvite);
      
      if (result.insufficientSeats) {
        // Show dialog asking if they want to send invite anyway
        setInviteTarget({
          listaAttesaId,
          name,
          postiDisponibili: result.postiDisponibili || 0,
          postiRichiesti: result.postiRichiesti || 0,
        });
        onInviteDialogOpen();
        return;
      }
      
      toast({
        title: "Invito inviato",
        description: `L'invito è stato inviato a ${name}`,
        status: "success",
        duration: 5000,
      });
      // Refresh data to show updated Invito status
      loadData();
    } catch (err: any) {
      toast({
        title: "Errore",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setInviting(null);
    }
  };

  const handleForceInvite = async () => {
    if (!inviteTarget) return;
    onInviteDialogClose();
    await handleInvite(inviteTarget.listaAttesaId, inviteTarget.name, true);
  };

  const handleShowNote = (name: string, note: string) => {
    setNoteTarget({ name, note });
    onNoteOpen();
  };

  const handleSendReminders = async () => {
    if (!evento || evento.EventoId === undefined) return;
    if (
      !window.confirm(
        `Vuoi inviare il promemoria dell'evento a tutti i partecipanti (${activePrenotazioni.length} prenotazioni)?`
      )
    ) {
      return;
    }

    try {
      setSendingReminders(true);
      const result = await bookingService.sendEventReminders(evento.EventoId);
      toast({
        title: result.failed.length === 0 ? "Promemoria inviati" : "Promemoria inviati parzialmente",
        description:
          result.failed.length === 0
            ? `${result.sent} email inviate`
            : `${result.sent} inviate, ${result.failed.length} fallite: ${result.failed.map((f) => f.email).join(", ")}`,
        status: result.failed.length === 0 ? "success" : "warning",
        duration: 8000,
        isClosable: true,
      });
    } catch (err: any) {
      toast({
        title: "Errore",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setSendingReminders(false);
    }
  };

  const handleConvertClick =(l: ListaAttesa) => {
    if (l.ListaAttesaId === undefined) return;
    const postiDisponibili = (evento?.PostiTotali || 0) - totalGuests;
    setConvertTarget({
      listaAttesaId: l.ListaAttesaId,
      name: `${l.Nome} ${l.Cognome}`,
      email: l.Email,
      postiDisponibili,
      postiRichiesti: l.NumeroPersone,
    });
    onConvertOpen();
  };

  const handleConfirmConvert = async (sendEmail: boolean) => {
    if (!convertTarget) return;

    try {
      setConverting(true);
      const result = await bookingService.convertListaAttesa(convertTarget.listaAttesaId, sendEmail);
      onConvertClose();
      toast({
        title: "Prenotazione creata",
        description:
          sendEmail && !result.emailSent
            ? `${convertTarget.name} è stato convertito, ma l'invio dell'email è fallito`
            : `${convertTarget.name} è stato convertito in prenotazione${result.emailSent ? " e l'email è stata inviata" : ""}`,
        status: sendEmail && !result.emailSent ? "warning" : "success",
        duration: 5000,
      });
      loadData();
    } catch (err: any) {
      toast({
        title: "Errore",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setConverting(false);
    }
  };

  const handleResendConfirmation = async (prenotazioneId: number, name: string, email: string) => {
    if (!window.confirm(`Vuoi reinviare l'email di conferma a ${name} (${email})?`)) {
      return;
    }

    try {
      setResending(prenotazioneId);
      await bookingService.resendConfirmation(prenotazioneId);
      toast({
        title: "Email inviata",
        description: `L'email di conferma è stata reinviata a ${email}`,
        status: "success",
        duration: 5000,
      });
    } catch (err: any) {
      toast({
        title: "Errore",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setResending(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      if (deleteTarget.type === "prenotazione") {
        if (deleteTarget.isCancelled) {
          // Permanently delete cancelled reservation
          await bookingService.permanentDeletePrenotazione(deleteTarget.id);
          toast({
            title: "Prenotazione eliminata definitivamente",
            status: "success",
            duration: 3000,
          });
        } else {
          // Cancel active reservation
          await bookingService.deletePrenotazione(deleteTarget.id);
          toast({
            title: "Prenotazione annullata",
            status: "success",
            duration: 3000,
          });
        }
      } else {
        await bookingService.deleteListaAttesa(deleteTarget.id);
        toast({
          title: "Iscrizione lista attesa eliminata",
          status: "success",
          duration: 3000,
        });
      }
      onClose();
      loadData();
    } catch (err: any) {
      toast({
        title: "Errore",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const calculatePrenotazioneTotal = (p: Prenotazione) => {
    if (!evento) return 0;
    const subtotale = 
      p.NumeroAlcolico * (evento.PrezzoAlcolico || 0) +
      p.NumeroAnalcolico * (evento.PrezzoAnalcolico || 0);
    
    // Calculate gift card discount if present
    const giftCardDiscount = (p as any).GiftCardCodice
      ? ((p as any).GiftCardNumeroAlcolico || 0) * (evento.PrezzoAlcolico || 0) +
        ((p as any).GiftCardNumeroAnalcolico || 0) * (evento.PrezzoAnalcolico || 0)
      : 0;
    
    return Math.max(0, subtotale - giftCardDiscount);
  };

  const calculateGiftCardDiscount = (p: Prenotazione) => {
    if (!evento || !(p as any).GiftCardCodice) return 0;
    return ((p as any).GiftCardNumeroAlcolico || 0) * (evento.PrezzoAlcolico || 0) +
           ((p as any).GiftCardNumeroAnalcolico || 0) * (evento.PrezzoAnalcolico || 0);
  };

  // Filter out cancelled reservations for active count
  const activePrenotazioni = prenotazioni.filter((p: any) => !p.Annullata);
  const cancelledPrenotazioni = prenotazioni.filter((p: any) => p.Annullata);

  const totalGuests = activePrenotazioni.reduce((sum, p) => sum + p.NumeroPersone, 0);
  const totalAlcolico = activePrenotazioni.reduce((sum, p) => sum + p.NumeroAlcolico, 0);
  const totalAnalcolico = activePrenotazioni.reduce((sum, p) => sum + p.NumeroAnalcolico, 0);
  const totalRevenue = activePrenotazioni.reduce(
    (sum, p) => sum + calculatePrenotazioneTotal(p),
    0
  );

  const isMobile = useBreakpointValue({ base: true, md: false });

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="teal.500" />
        <Text mt={4} color="gray.400">
          Caricamento...
        </Text>
      </Box>
    );
  }

  if (error || !evento) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error || "Evento non trovato"}
      </Alert>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Stack direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "stretch", md: "center" }} spacing={4}>
        <HStack>
          <IconButton
            aria-label="Torna indietro"
            icon={<ArrowBackIcon />}
            variant="ghost"
            onClick={() => navigate("/")}
          />
          <Box>
            <Heading size={{ base: "md", md: "lg" }} color="white">
              {evento.Titolo}
            </Heading>
            <Text color="gray.400" textTransform="capitalize" fontSize={{ base: "sm", md: "md" }}>
              📅 {formatDate(evento.DataEvento)}
            </Text>
          </Box>
        </HStack>
        <Stack direction={{ base: "column", sm: "row" }} spacing={2}>
          <Button
            leftIcon={<EmailIcon />}
            variant="outline"
            size={{ base: "sm", md: "md" }}
            w={{ base: "full", md: "auto" }}
            isLoading={sendingReminders}
            isDisabled={activePrenotazioni.length === 0 || new Date(evento.DataEvento) < new Date()}
            onClick={handleSendReminders}
          >
            Invia promemoria
          </Button>
          <Button
            as={RouterLink}
            to={`/eventi/${evento.EventoId}`}
            leftIcon={<EditIcon />}
            variant="primary"
            size={{ base: "sm", md: "md" }}
            w={{ base: "full", md: "auto" }}
          >
            Modifica Evento
          </Button>
        </Stack>
      </Stack>

      {/* Event Image */}
      {evento.ImmagineUrl && (
        <Box borderRadius="lg" overflow="hidden" maxH="300px">
          <Image
            src={evento.ImmagineUrl}
            alt={evento.Titolo}
            w="full"
            h="300px"
            objectFit="cover"
          />
        </Box>
      )}

      {/* Stats */}
      <Box bg="gray.800" p={{ base: 4, md: 6 }} borderRadius="lg" borderWidth="1px" borderColor="gray.700">
        <SimpleGrid columns={{ base: 2, sm: 3, md: 5 }} spacing={{ base: 3, md: 4 }}>
          <Stat>
            <StatLabel color="gray.400" fontSize={{ base: "xs", md: "sm" }}>Prenotazioni</StatLabel>
            <StatNumber color="white" fontSize={{ base: "xl", md: "2xl" }}>{activePrenotazioni.length}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel color="gray.400" fontSize={{ base: "xs", md: "sm" }}>Ospiti</StatLabel>
            <StatNumber color="white" fontSize={{ base: "xl", md: "2xl" }}>
              {totalGuests}/{evento.PostiTotali}
            </StatNumber>
          </Stat>
          <Stat>
            <StatLabel color="gray.400" fontSize={{ base: "xs", md: "sm" }}>🍷 Alcolico</StatLabel>
            <StatNumber color="white" fontSize={{ base: "xl", md: "2xl" }}>{totalAlcolico}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel color="gray.400" fontSize={{ base: "xs", md: "sm" }}>🥤 Analcolico</StatLabel>
            <StatNumber color="white" fontSize={{ base: "xl", md: "2xl" }}>{totalAnalcolico}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel color="gray.400" fontSize={{ base: "xs", md: "sm" }}>Totale</StatLabel>
            <StatNumber color="teal.300" fontSize={{ base: "xl", md: "2xl" }}>{formatCurrency(totalRevenue)}</StatNumber>
          </Stat>
        </SimpleGrid>
      </Box>

      {/* Tabs */}
      <Tabs variant="enclosed">
        <TabList overflowX="auto" overflowY="hidden" css={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
          <Tab color="gray.400" _selected={{ color: "white", bg: "gray.700" }} fontSize={{ base: "sm", md: "md" }} whiteSpace="nowrap">
            Prenotazioni ({activePrenotazioni.length})
          </Tab>
          <Tab color="gray.400" _selected={{ color: "white", bg: "gray.700" }} fontSize={{ base: "sm", md: "md" }} whiteSpace="nowrap">
            Lista d'Attesa ({listaAttesa.length})
          </Tab>
          {cancelledPrenotazioni.length > 0 && (
            <Tab color="gray.400" _selected={{ color: "white", bg: "gray.700" }} fontSize={{ base: "sm", md: "md" }} whiteSpace="nowrap">
              Annullate ({cancelledPrenotazioni.length})
            </Tab>
          )}
        </TabList>

        <TabPanels>
          {/* Active Reservations */}
          <TabPanel px={0}>
            {activePrenotazioni.length === 0 ? (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                Nessuna prenotazione per questo evento
              </Alert>
            ) : isMobile ? (
              /* Mobile Card Layout */
              <VStack spacing={3} align="stretch">
                {activePrenotazioni.map((p: any) => (
                  <Card key={p.PrenotazioneId} bg="gray.700" size="sm">
                    <CardBody>
                      <Flex justify="space-between" align="start" mb={2}>
                        <Box>
                          <HStack spacing={2}>
                            <Text color="white" fontWeight="bold" fontSize="md">
                              {p.Nome} {p.Cognome}
                            </Text>
                            {p.Note && (
                              <Tooltip label={p.Note} placement="top" hasArrow>
                                <ChatIcon color="teal.400" boxSize={3} cursor="pointer" onClick={() => handleShowNote(`${p.Nome} ${p.Cognome}`, p.Note)} />
                              </Tooltip>
                            )}
                          </HStack>
                          <HStack spacing={1}>
                            <Text fontSize="xs" color="gray.400">{p.Email}</Text>
                            {p.EmailInviata === false && (
                              <Tooltip label={p.EmailErrore || "Email non inviata"} placement="top" hasArrow>
                                <WarningIcon color="red.400" boxSize={3} />
                              </Tooltip>
                            )}
                            {p.EmailInviata === true && (
                              <Tooltip label={`Email inviata il ${new Date(p.EmailDataInvio).toLocaleString('it-IT')}`} placement="top" hasArrow>
                                <CheckCircleIcon color="green.400" boxSize={3} />
                              </Tooltip>
                            )}
                          </HStack>
                          <Text fontSize="xs" color="gray.500">{p.Telefono}</Text>
                        </Box>
                        <HStack spacing={1}>
                          <Tooltip label="Reinvia email conferma">
                            <IconButton
                              aria-label="Reinvia conferma"
                              icon={<EmailIcon />}
                              size="sm"
                              variant="action"
                              isLoading={resending === p.PrenotazioneId}
                              onClick={() =>
                                handleResendConfirmation(p.PrenotazioneId, `${p.Nome} ${p.Cognome}`, p.Email)
                              }
                            />
                          </Tooltip>
                          <Tooltip label="Modifica prenotazione">
                            <IconButton
                              aria-label="Modifica"
                              icon={<EditIcon />}
                              size="sm"
                              variant="action"
                              onClick={() => navigate(`/modifica/${p.PrenotazioneId}?from=admin&eventoId=${id}`)}
                            />
                          </Tooltip>
                          <IconButton
                            aria-label="Elimina"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="danger-ghost"
                            onClick={() =>
                              handleDeleteClick("prenotazione", p.PrenotazioneId, `${p.Nome} ${p.Cognome}`)
                            }
                          />
                        </HStack>
                      </Flex>
                      <SimpleGrid columns={4} spacing={2} mt={2}>
                        <Tooltip 
                          label={p.NomiPartecipanti && JSON.parse(p.NomiPartecipanti).length > 0 
                            ? JSON.parse(p.NomiPartecipanti).join(', ') 
                            : 'Nessun nome specificato'} 
                          placement="top" 
                          hasArrow
                        >
                          <Box textAlign="center" bg="gray.600" p={2} borderRadius="md" cursor="pointer">
                            <Text fontSize="xs" color="gray.400">Persone</Text>
                            <Text fontWeight="bold" color="white">{p.NumeroPersone}</Text>
                          </Box>
                        </Tooltip>
                        <Box textAlign="center" bg="gray.600" p={2} borderRadius="md">
                          <Text fontSize="xs" color="gray.400">🍷</Text>
                          <Text fontWeight="bold" color="white">{p.NumeroAlcolico}</Text>
                        </Box>
                        <Box textAlign="center" bg="gray.600" p={2} borderRadius="md">
                          <Text fontSize="xs" color="gray.400">🥤</Text>
                          <Text fontWeight="bold" color="white">{p.NumeroAnalcolico}</Text>
                        </Box>
                        <Box textAlign="center" bg="teal.700" p={2} borderRadius="md">
                          <Text fontSize="xs" color="gray.300">Totale</Text>
                          <Text fontWeight="bold" color="teal.200" fontSize="sm">{formatCurrency(calculatePrenotazioneTotal(p))}</Text>
                        </Box>
                      </SimpleGrid>
                      {(p as any).GiftCardCodice && (
                        <Box mt={2} p={2} bg="purple.800" borderRadius="md">
                          <HStack justify="space-between">
                            <Text fontSize="xs" color="purple.200">🎁 Gift Card: {(p as any).GiftCardCodice}</Text>
                            <Text fontSize="xs" color="green.300">-{formatCurrency(calculateGiftCardDiscount(p))}</Text>
                          </HStack>
                        </Box>
                      )}
                      {(p.CodiceFiscale || p.PartitaIVA) && (
                        <Box mt={2} pt={2} borderTop="1px" borderColor="gray.600">
                          {p.CodiceFiscale && <Text fontSize="xs" color="gray.400">CF: {p.CodiceFiscale}</Text>}
                          {p.PartitaIVA && <Text fontSize="xs" color="gray.400">P.IVA: {p.PartitaIVA}</Text>}
                          {p.RagioneSociale && <Text fontSize="xs" color="gray.500">{p.RagioneSociale}</Text>}
                        </Box>
                      )}
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            ) : (
              /* Desktop Table Layout */
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color="gray.400">Nome</Th>
                      <Th color="gray.400">Contatto</Th>
                      <Th color="gray.400">Fatturazione</Th>
                      <Th color="gray.400" isNumeric>Persone</Th>
                      <Th color="gray.400" isNumeric>🍷</Th>
                      <Th color="gray.400" isNumeric>🥤</Th>
                      <Th color="gray.400" isNumeric>Totale</Th>
                      <Th color="gray.400">Data</Th>
                      <Th color="gray.400" w="50px"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {activePrenotazioni.map((p: any) => (
                      <Tr key={p.PrenotazioneId}>
                        <Td color="white" fontWeight="medium">
                          <HStack spacing={2}>
                            <Text>{p.Nome} {p.Cognome}</Text>
                            {p.Note && (
                              <Tooltip label={p.Note} placement="top" hasArrow bg="gray.200">
                                <ChatIcon color="teal.400" boxSize={3} cursor="pointer" onClick={() => handleShowNote(`${p.Nome} ${p.Cognome}`, p.Note)} />
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Text fontSize="sm" color="gray.300">{p.Email}</Text>
                            {p.EmailInviata === false && (
                              <Tooltip label={p.EmailErrore || "Email non inviata"} placement="top" hasArrow>
                                <WarningIcon color="red.400" boxSize={3} />
                              </Tooltip>
                            )}
                            {p.EmailInviata === true && (
                              <Tooltip label={`Email inviata il ${new Date(p.EmailDataInvio).toLocaleString('it-IT')}`} placement="top" hasArrow>
                                <CheckCircleIcon color="green.400" boxSize={3} />
                              </Tooltip>
                            )}
                          </HStack>
                          <Text fontSize="xs" color="gray.500">{p.Telefono}</Text>
                        </Td>
                        <Td>
                          {p.CodiceFiscale && (
                            <Text fontSize="xs" color="gray.400">CF: {p.CodiceFiscale}</Text>
                          )}
                          {p.PartitaIVA && (
                            <Text fontSize="xs" color="gray.400">P.IVA: {p.PartitaIVA}</Text>
                          )}
                          {p.RagioneSociale && (
                            <Text fontSize="xs" color="gray.500">{p.RagioneSociale}</Text>
                          )}
                          {(p.Indirizzo || p.Citta) && (
                            <Text fontSize="xs" color="gray.500">
                              {[p.Indirizzo, p.CAP, p.Citta, p.Provincia].filter(Boolean).join(", ")}
                            </Text>
                          )}
                        </Td>
                        <Td isNumeric color="white">
                          <Tooltip 
                            label={p.NomiPartecipanti && JSON.parse(p.NomiPartecipanti).length > 0 
                              ? JSON.parse(p.NomiPartecipanti).join(', ') 
                              : 'Nessun nome specificato'} 
                            placement="top" 
                            hasArrow
                          >
                            <Text as="span" cursor="pointer">{p.NumeroPersone}</Text>
                          </Tooltip>
                        </Td>
                        <Td isNumeric color="white">{p.NumeroAlcolico}</Td>
                        <Td isNumeric color="white">{p.NumeroAnalcolico}</Td>
                        <Td isNumeric color="teal.300" fontWeight="medium">
                          {formatCurrency(calculatePrenotazioneTotal(p))}
                          {(p as any).GiftCardCodice && (
                            <Tooltip label={`Gift Card ${(p as any).GiftCardCodice}: -${formatCurrency(calculateGiftCardDiscount(p))}`} placement="top" hasArrow>
                              <Badge ml={2} variant="subtle" fontSize="xs">🎁</Badge>
                            </Tooltip>
                          )}
                        </Td>
                        <Td color="gray.400" fontSize="xs">
                          {new Date(p.DataPrenotazione).toLocaleDateString("it-IT")}
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Tooltip label="Reinvia email conferma" placement="top">
                              <IconButton
                                aria-label="Reinvia conferma"
                                icon={<EmailIcon />}
                                size="sm"
                                variant="action"
                                isLoading={resending === p.PrenotazioneId}
                                onClick={() =>
                                  handleResendConfirmation(p.PrenotazioneId, `${p.Nome} ${p.Cognome}`, p.Email)
                                }
                              />
                            </Tooltip>
                            <Tooltip label="Modifica prenotazione" placement="top">
                              <IconButton
                                aria-label="Modifica"
                                icon={<EditIcon />}
                                size="sm"
                                variant="action"
                                onClick={() => navigate(`/modifica/${p.PrenotazioneId}?from=admin&eventoId=${id}`)}
                              />
                            </Tooltip>
                            <IconButton
                              aria-label="Elimina"
                              icon={<DeleteIcon />}
                              size="sm"
                              variant="danger-ghost"
                              onClick={() =>
                                handleDeleteClick(
                                  "prenotazione",
                                  p.PrenotazioneId,
                                  `${p.Nome} ${p.Cognome}`
                                )
                              }
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>

          {/* Waiting List */}
          <TabPanel px={0}>
            {listaAttesa.length === 0 ? (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                Nessuno in lista d'attesa
              </Alert>
            ) : isMobile ? (
              /* Mobile Card Layout */
              <VStack spacing={3} align="stretch">
                {listaAttesa.map((l: any, index: number) => (
                  <Card key={l.ListaAttesaId} bg="gray.700" size="sm">
                    <CardBody>
                      <Flex justify="space-between" align="start" mb={2}>
                        <Box>
                          <HStack spacing={2} mb={1}>
                            <Badge variant="number">{index + 1}</Badge>
                            {l.Invito && <Badge variant="subtle" fontSize="xs">Invito</Badge>}</HStack>
                          <HStack spacing={2}>
                            <Text color="white" fontWeight="bold" fontSize="md">
                              {l.Nome} {l.Cognome}
                            </Text>
                            {l.Note && (
                              <Tooltip label={l.Note} placement="top" hasArrow>
                                <ChatIcon color="teal.400" boxSize={3} cursor="pointer" onClick={() => handleShowNote(`${l.Nome} ${l.Cognome}`, l.Note)} />
                              </Tooltip>
                            )}
                          </HStack>
                          <HStack spacing={1}>
                            <Text fontSize="xs" color="gray.400">{l.Email}</Text>
                            {l.EmailInviata === false && (
                              <Tooltip label={l.EmailErrore || "Email non inviata"} placement="top" hasArrow>
                                <WarningIcon color="red.400" boxSize={3} />
                              </Tooltip>
                            )}
                            {l.EmailInviata === true && (
                              <Tooltip label={`Email inviata il ${new Date(l.EmailDataInvio).toLocaleString('it-IT')}`} placement="top" hasArrow>
                                <CheckCircleIcon color="green.400" boxSize={3} />
                              </Tooltip>
                            )}
                          </HStack>
                          <Text fontSize="xs" color="gray.500">{l.Telefono}</Text>
                        </Box>
                        <HStack spacing={1}>
                          <Tooltip label={l.Invito ? "Reinvia invito" : "Invia invito"}>
                            <IconButton
                              aria-label="Invita"
                              icon={<EmailIcon />}
                              size="sm"
                              variant="action"
                              isLoading={inviting === l.ListaAttesaId}
                              onClick={() => handleInvite(l.ListaAttesaId, `${l.Nome} ${l.Cognome}`)}
                            />
                          </Tooltip>
                          <Tooltip label="Converti in prenotazione">
                            <IconButton
                              aria-label="Converti in prenotazione"
                              icon={<CheckCircleIcon />}
                              size="sm"
                              variant="action"
                              onClick={() => handleConvertClick(l)}
                            />
                          </Tooltip>
                          <Tooltip label="Modifica iscrizione">
                            <IconButton
                              aria-label="Modifica"
                              icon={<EditIcon />}
                              size="sm"
                              variant="action"
                              onClick={() => navigate(`/modifica-attesa/${l.ListaAttesaId}?from=admin&eventoId=${id}`)}
                            />
                          </Tooltip>
                          <IconButton
                            aria-label="Elimina"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="danger-ghost"
                            onClick={() => handleDeleteClick("listaAttesa", l.ListaAttesaId, `${l.Nome} ${l.Cognome}`)}
                          />
                        </HStack>
                      </Flex>
                      <SimpleGrid columns={3} spacing={2} mt={2}>
                        <Tooltip 
                          label={l.NomiPartecipanti && JSON.parse(l.NomiPartecipanti).length > 0 
                            ? JSON.parse(l.NomiPartecipanti).join(', ') 
                            : 'Nessun nome specificato'} 
                          placement="top" 
                          hasArrow
                        >
                          <Box textAlign="center" bg="gray.600" p={2} borderRadius="md" cursor="pointer">
                            <Text fontSize="xs" color="gray.400">Persone</Text>
                            <Text fontWeight="bold" color="white">{l.NumeroPersone}</Text>
                          </Box>
                        </Tooltip>
                        <Box textAlign="center" bg="gray.600" p={2} borderRadius="md">
                          <Text fontSize="xs" color="gray.400">🍷</Text>
                          <Text fontWeight="bold" color="white">{l.NumeroAlcolico}</Text>
                        </Box>
                        <Box textAlign="center" bg="gray.600" p={2} borderRadius="md">
                          <Text fontSize="xs" color="gray.400">🥤</Text>
                          <Text fontWeight="bold" color="white">{l.NumeroAnalcolico}</Text>
                        </Box>
                      </SimpleGrid>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            ) : (
              /* Desktop Table Layout */
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color="gray.400">#</Th>
                      <Th color="gray.400">Nome</Th>
                      <Th color="gray.400">Contatto</Th>
                      <Th color="gray.400" isNumeric>Persone</Th>
                      <Th color="gray.400" isNumeric>🍷</Th>
                      <Th color="gray.400" isNumeric>🥤</Th>
                      <Th color="gray.400">Data</Th>
                      <Th color="gray.400" w="100px"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {listaAttesa.map((l: any, index: number) => (
                      <Tr key={l.ListaAttesaId}>
                        <Td>
                          <HStack spacing={2}>
                            <Badge variant="number">{index + 1}</Badge>
                            {l.Invito && (
                              <Badge variant="subtle" fontSize="xs">Invito</Badge>
                            )}
                          </HStack>
                        </Td>
                        <Td color="white" fontWeight="medium">
                          <HStack spacing={2}>
                            <Text>{l.Nome} {l.Cognome}</Text>
                            {l.Note && (
                              <Tooltip label={l.Note} placement="top" hasArrow bg="gray.700">
                                <ChatIcon color="teal.400" boxSize={3} cursor="pointer" onClick={() => handleShowNote(`${l.Nome} ${l.Cognome}`, l.Note)} />
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Text fontSize="sm" color="gray.300">{l.Email}</Text>
                            {l.EmailInviata === false && (
                              <Tooltip label={l.EmailErrore || "Email non inviata"} placement="top" hasArrow>
                                <WarningIcon color="red.400" boxSize={3} />
                              </Tooltip>
                            )}
                            {l.EmailInviata === true && (
                              <Tooltip label={`Email inviata il ${new Date(l.EmailDataInvio).toLocaleString('it-IT')}`} placement="top" hasArrow>
                                <CheckCircleIcon color="green.400" boxSize={3} />
                              </Tooltip>
                            )}
                          </HStack>
                          <Text fontSize="xs" color="gray.500">{l.Telefono}</Text>
                        </Td>
                        <Td isNumeric color="white">
                          <Tooltip 
                            label={l.NomiPartecipanti && JSON.parse(l.NomiPartecipanti).length > 0 
                              ? JSON.parse(l.NomiPartecipanti).join(', ') 
                              : 'Nessun nome specificato'} 
                            placement="top" 
                            hasArrow
                          >
                            <Text as="span" cursor="pointer">{l.NumeroPersone}</Text>
                          </Tooltip>
                        </Td>
                        <Td isNumeric color="white">{l.NumeroAlcolico}</Td>
                        <Td isNumeric color="white">{l.NumeroAnalcolico}</Td>
                        <Td color="gray.400" fontSize="xs">
                          {new Date(l.DataRichiesta).toLocaleDateString("it-IT")}
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Tooltip label={l.Invito ? "Reinvia invito" : "Invia invito"} placement="top">
                              <IconButton
                                aria-label="Invita"
                                icon={<EmailIcon />}
                                size="sm"
                                variant="action"
                                isLoading={inviting === l.ListaAttesaId}
                                onClick={() =>
                                  handleInvite(l.ListaAttesaId, `${l.Nome} ${l.Cognome}`)
                                }
                              />
                            </Tooltip>
                            <Tooltip label="Converti in prenotazione" placement="top">
                              <IconButton
                                aria-label="Converti in prenotazione"
                                icon={<CheckCircleIcon />}
                                size="sm"
                                variant="action"
                                onClick={() => handleConvertClick(l)}
                              />
                            </Tooltip>
                            <Tooltip label="Modifica iscrizione" placement="top">
                              <IconButton
                                aria-label="Modifica"
                                icon={<EditIcon />}
                                size="sm"
                                variant="action"
                                onClick={() => navigate(`/modifica-attesa/${l.ListaAttesaId}?from=admin&eventoId=${id}`)}
                              />
                            </Tooltip>
                            <IconButton
                              aria-label="Elimina"
                              icon={<DeleteIcon />}
                              size="sm"
                              variant="danger-ghost"
                              onClick={() =>
                                handleDeleteClick(
                                  "listaAttesa",
                                  l.ListaAttesaId,
                                  `${l.Nome} ${l.Cognome}`
                                )
                              }
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>

          {/* Cancelled Reservations */}
          {cancelledPrenotazioni.length > 0 && (
            <TabPanel px={0}>
              {isMobile ? (
                /* Mobile Card Layout */
                <VStack spacing={3} align="stretch">
                  {cancelledPrenotazioni.map((p: any) => (
                    <Card key={p.PrenotazioneId} bg="gray.700" size="sm" opacity={0.6}>
                      <CardBody>
                        <Flex justify="space-between" align="start">
                          <Box>
                            <HStack spacing={2}>
                              <Text color="white" fontWeight="bold">
                                {p.Nome} {p.Cognome}
                              </Text>
                              <Badge variant="cancelled" fontSize="xs">Annullata</Badge>
                            </HStack>
                            <Text fontSize="xs" color="gray.400">{p.Email}</Text>
                            <Text fontSize="xs" color="gray.500">Persone: {p.NumeroPersone}</Text>
                          </Box>
                          <IconButton
                            aria-label="Elimina"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="danger-ghost"
                            onClick={() => handleDeleteClick("prenotazione", p.PrenotazioneId, `${p.Nome} ${p.Cognome}`, true)}
                          />
                        </Flex>
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              ) : (
                /* Desktop Table Layout */
                <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color="gray.400">Nome</Th>
                      <Th color="gray.400">Contatto</Th>
                      <Th color="gray.400" isNumeric>Persone</Th>
                      <Th color="gray.400">Data</Th>
                      <Th color="gray.400" w="50px"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {cancelledPrenotazioni.map((p: any) => (
                      <Tr key={p.PrenotazioneId} opacity={0.6}>
                        <Td color="white">
                          {p.Nome} {p.Cognome}
                          <Badge ml={2} variant="cancelled" fontSize="xs">
                            Annullata
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color="gray.300">{p.Email}</Text>
                        </Td>
                        <Td isNumeric color="white">{p.NumeroPersone}</Td>
                        <Td color="gray.400" fontSize="xs">
                          {new Date(p.DataPrenotazione).toLocaleDateString("it-IT")}
                        </Td>
                        <Td>
                          <IconButton
                            aria-label="Elimina"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="danger-ghost"
                            onClick={() =>
                              handleDeleteClick(
                                "prenotazione",
                                p.PrenotazioneId,
                                `${p.Nome} ${p.Cognome}`,
                                true
                              )
                            }
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              )}
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="gray.800">
          <ModalHeader color="white">
            {deleteTarget?.isCancelled ? "Conferma Eliminazione Definitiva" : "Conferma Annullamento"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text color="gray.300">
              {deleteTarget?.isCancelled ? (
                <>
                  Sei sicuro di voler eliminare definitivamente la prenotazione di{" "}
                  <strong>{deleteTarget?.name}</strong>?
                </>
              ) : deleteTarget?.type === "prenotazione" ? (
                <>
                  Sei sicuro di voler annullare la prenotazione di{" "}
                  <strong>{deleteTarget?.name}</strong>?
                </>
              ) : (
                <>
                  Sei sicuro di voler eliminare l'iscrizione alla lista d'attesa di{" "}
                  <strong>{deleteTarget?.name}</strong>?
                </>
              )}
            </Text>
            <Text color="red.300" mt={2} fontSize="sm">
              {deleteTarget?.isCancelled 
                ? "Il record verrà rimosso definitivamente dal database."
                : "Questa azione non può essere annullata."}
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose}>
              Annulla
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              isLoading={deleting}
            >
              {deleteTarget?.isCancelled ? "Elimina Definitivamente" : "Conferma"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Notes Dialog */}
      <Modal isOpen={isNoteOpen} onClose={onNoteClose} isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="gray.800" mx={4}>
          <ModalHeader color="white">Note di {noteTarget?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text color="gray.200" whiteSpace="pre-wrap">{noteTarget?.note}</Text>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Convert Waiting List Dialog */}
      <Modal isOpen={isConvertOpen} onClose={onConvertClose} isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="gray.800">
          <ModalHeader color="white">Converti in Prenotazione</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {convertTarget && convertTarget.postiDisponibili < convertTarget.postiRichiesti && (
              <Alert status="warning" bg="orange.900" color="white" borderRadius="md" mb={4}>
                <AlertIcon color="orange.300" />
                <Box>
                  <Text fontWeight="bold">Posti insufficienti</Text>
                  <Text fontSize="sm">
                    Posti disponibili: {convertTarget.postiDisponibili} | Posti richiesti: {convertTarget.postiRichiesti}
                  </Text>
                </Box>
              </Alert>
            )}
            <Text color="gray.300">
              Vuoi convertire <strong>{convertTarget?.name}</strong> in una prenotazione a tutti gli effetti?
            </Text>
            <Text color="gray.300" mt={3}>
              Vuoi inviare l'email di prenotazione confermata a <strong>{convertTarget?.email}</strong>?
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onConvertClose} isDisabled={converting}>
              Annulla
            </Button>
            <Button variant="outline" onClick={() => handleConfirmConvert(false)} isDisabled={converting}>
              Converti senza email
            </Button>
            <Button variant="primary" onClick={() => handleConfirmConvert(true)} isLoading={converting}>
              Converti e invia email
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Insufficient Seats Dialog */}
      <Modal isOpen={isInviteDialogOpen} onClose={onInviteDialogClose} isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="gray.800">
          <ModalHeader color="white">Posti Insufficienti</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" bg="orange.900" color="white" borderRadius="md" mb={4}>
              <AlertIcon color="orange.300" />
              <Box>
                <Text fontWeight="bold">Non ci sono abbastanza posti disponibili</Text>
                <Text fontSize="sm">
                  Posti disponibili: {inviteTarget?.postiDisponibili} | 
                  Posti richiesti: {inviteTarget?.postiRichiesti}
                </Text>
              </Box>
            </Alert>
            <Text color="gray.300">
              Vuoi comunque inviare l'invito a <strong>{inviteTarget?.name}</strong>?
            </Text>
            <Text color="gray.400" mt={2} fontSize="sm">
              Verrà inviata solo l'email di invito, senza creare una prenotazione effettiva.
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onInviteDialogClose}>
              Annulla
            </Button>
            <Button
              colorScheme="orange"
              onClick={handleForceInvite}
              isLoading={inviting === inviteTarget?.listaAttesaId}
            >
              Invia Comunque
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default EventDetails;
